import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { apiFetch } from './api';

export default function EquipmentTasks({ user, compactMode = false, focusRequest = null }) {
  const canUpdateTasks = user?.permissions?.includes?.("maintenance:update") ?? user?.can_update_maintenance ?? false;
  const canEditAssetStatus = canUpdateTasks && (user?.can_manage_equipment ?? false);
  const [tasks, setTasks] = useState([]);
  const [boatConfig, setBoatConfig] = useState({ hulls: [], oars: [], coxBoxes: [], launches: [], speedCoaches: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [newTaskModalOpen, setNewTaskModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [assetStatuses, setAssetStatuses] = useState([]);
  const [assetStatusInitial, setAssetStatusInitial] = useState('');
  const [coaches, setCoaches] = useState([]);

  const [hideCompleted, setHideCompleted] = useState(true);
  const [myTasksOnly, setMyTasksOnly] = useState(false);

  const [newTask, setNewTask] = useState({
    equipmentType: 'none',
    equipmentId: '',
    equipmentName: '',
    description: ''
  });
  const [photoState, setPhotoState] = useState({ preview: '', data: '', type: '' });
  const [photoInputKey, setPhotoInputKey] = useState(0);

  const assetTypeOptions = useMemo(() => {
    const options = [{ value: 'none', label: 'No specific equipment' }];
    if ((boatConfig.hulls || []).length) options.push({ value: 'hull', label: 'Hull' });
    if ((boatConfig.oars || []).length) options.push({ value: 'oars', label: 'Oars' });
    if ((boatConfig.coxBoxes || []).length) options.push({ value: 'cox_boxes', label: 'Cox Boxes' });
    if ((boatConfig.launches || []).length) options.push({ value: 'launch', label: 'Launch' });
    if ((boatConfig.speedCoaches || []).length) options.push({ value: 'speed_coach', label: 'Speed Coaches' });
    if (options.length === 1) {
      options.push(
        { value: 'hull', label: 'Hull' },
        { value: 'oars', label: 'Oars' },
        { value: 'cox_boxes', label: 'Cox Boxes' },
        { value: 'launch', label: 'Launch' },
        { value: 'speed_coach', label: 'Speed Coaches' },
      );
    }
    return options;
  }, [boatConfig]);


  const resetPhotoState = useCallback(() => {
    setPhotoState({ preview: '', data: '', type: '' });
    setPhotoInputKey((k) => k + 1);
  }, []);

  const resetNewTaskForm = useCallback(() => {
    setNewTask({
      equipmentType: 'none',
      equipmentId: '',
      equipmentName: '',
      description: ''
    });
    resetPhotoState();
  }, [resetPhotoState]);

  function handlePhotoChange(e) {
    const file = e.target.files?.[0];
    if (!file) {
      resetPhotoState();
      return;
    }
    const maxBytes = 5 * 1024 * 1024;
    if (file.size > maxBytes) {
      alert('Photo too large. Maximum size is 5MB.');
      resetPhotoState();
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        const base64 = reader.result.split(',')[1] || '';
        setPhotoState({
          preview: reader.result,
          data: base64,
          type: file.type || 'image/jpeg'
        });
      }
    };
    reader.readAsDataURL(file);
  }

  function clearPhoto() {
    resetPhotoState();
  }

  const openNewTaskModal = useCallback(() => {
    resetNewTaskForm();
    setNewTaskModalOpen(true);
  }, [resetNewTaskForm]);

  const closeNewTaskModal = useCallback(() => {
    setNewTaskModalOpen(false);
    resetNewTaskForm();
  }, [resetNewTaskForm]);

  async function loadTasks() {
    try {
      setLoading(true);
      setError('');
      const data = await apiFetch('/equipment-tasks');
      setTasks(data);
    } catch (e) {
      setError(e.message || 'Failed to load tasks');
    } finally {
      setLoading(false);
    }
  }

  async function loadBoatConfig() {
    try {
      const data = await apiFetch('/boat-config');
      const assets = data?.assets || {};
      setBoatConfig({
        hulls: Array.isArray(assets.hull) ? assets.hull : (data?.hulls || []),
        oars: Array.isArray(assets.oars) ? assets.oars : (data?.oars || []),
        coxBoxes: Array.isArray(assets.cox_boxes) ? assets.cox_boxes : (data?.coxBoxes || []),
        launches: Array.isArray(assets.launch) ? assets.launch : (data?.launches || []),
        speedCoaches: Array.isArray(assets.speed_coach) ? assets.speed_coach : (data?.speedCoaches || [])
      });
    } catch (e) {
      console.error('Failed to load boat config:', e);
    }
  }

  async function loadAssetStatuses() {
    try {
      const data = await apiFetch('/asset-status');
      setAssetStatuses(data);
    } catch (e) {
      console.error('Failed to load asset statuses:', e);
    }
  }

  async function loadCoaches() {
    try {
      const data = await apiFetch('/roster');
      const users = Array.isArray(data) ? data : (data?.roster ?? []);
      const coachList = users.filter(u => u.role_key === 'coach');
      setCoaches(coachList);
    } catch (e) {
      console.error('Failed to load coaches:', e);
    }
  }

  useEffect(() => {
    loadTasks();
    loadBoatConfig();
    loadAssetStatuses();
    loadCoaches();
  }, []);

  // Allow external focus requests to open the new task modal
  const lastFocusTsRef = useRef(null);
  useEffect(() => {
    if (!focusRequest?.action) return;
    if (focusRequest?.ts === lastFocusTsRef.current) return;
    lastFocusTsRef.current = focusRequest.ts;
    if (focusRequest.action === "new-task") {
      openNewTaskModal();
    }
  }, [focusRequest?.action, focusRequest?.ts, openNewTaskModal]);

  async function createTask() {
    const needsName = newTask.equipmentType !== 'none';
    if ((needsName && !newTask.equipmentName.trim()) || !newTask.description.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    const payload = { ...newTask };
    if (photoState.data && photoState.type) {
      payload.photoData = photoState.data;
      payload.photoType = photoState.type;
    }

    try {
      await apiFetch('/equipment-tasks', {
        method: 'POST',
        body: JSON.stringify(payload)
      });
      resetNewTaskForm();
      setNewTaskModalOpen(false);
      await loadTasks();
    } catch (e) {
      alert(e.message || 'Failed to create task');
    }
  }

  const [editForm, setEditForm] = useState({
    status: '',
    repairNotes: '',
    assetStatusId: '',
    assignedTo: ''
  });

  const [dragOverCol, setDragOverCol] = useState(null);
  const dragTaskId = useRef(null);
  const [groupBy, setGroupBy] = useState('status');

  const assigneeColumns = useMemo(() => {
    const seen = new Map();
    tasks.forEach(t => {
      const key = t.assigned_to ?? null;
      if (!seen.has(key)) seen.set(key, t.assigned_to_name || null);
    });
    const cols = [];
    if (seen.has(null)) cols.push({ assigneeId: null, label: 'Unassigned', headerColor: '#9ca3af', headerBg: 'rgba(156,163,175,0.1)', headerBorder: 'rgba(156,163,175,0.25)' });
    seen.forEach((name, id) => {
      if (id !== null) cols.push({ assigneeId: id, label: name || `Coach #${id}`, headerColor: 'var(--accent)', headerBg: 'rgba(56,189,248,0.1)', headerBorder: 'rgba(56,189,248,0.25)' });
    });
    return cols;
  }, [tasks]);

  function openEditModal(task) {
    const linkedAsset = findAssetForTask(task);
    const initialAssetStatus = linkedAsset?.statusId ? String(linkedAsset.statusId) : '';
    setEditingTask(task.id);
    setEditForm({
      status: task.status || 'submitted',
      repairNotes: task.repair_notes || '',
      assetStatusId: initialAssetStatus,
      assignedTo: task.assigned_to ? String(task.assigned_to) : ''
    });
    setAssetStatusInitial(initialAssetStatus);
  }

  function closeEditModal() {
    setEditingTask(null);
    setAssetStatusInitial('');
    setEditForm({
      status: '',
      repairNotes: '',
      assetStatusId: '',
      assignedTo: ''
    });
  }

  async function updateTaskStatus() {
    if (!editingTask || !canUpdateTasks) return;
    const task = tasks.find(t => t.id === editingTask);
    if (!task) {
      alert('Task not found');
      return;
    }

    try {
      await apiFetch(`/equipment-tasks/${editingTask}`, {
        method: 'PATCH',
        body: JSON.stringify({
          status: editForm.status,
          repairNotes: editForm.repairNotes,
          assignedTo: editForm.assignedTo || null
        })
      });

      const hasLinkedAsset = Boolean(task.equipment_id);
      const assetStatusChanged = editForm.assetStatusId !== assetStatusInitial;
      if (canEditAssetStatus && hasLinkedAsset && assetStatusChanged) {
        const normalizedStatusId = editForm.assetStatusId ? Number(editForm.assetStatusId) : null;
        await apiFetch(`/boat-config/${task.equipment_id}`, {
          method: 'PATCH',
          body: JSON.stringify({ statusId: normalizedStatusId })
        });
        await loadBoatConfig();
      }

      await loadTasks();
      closeEditModal();
    } catch (e) {
      alert(e.message || 'Failed to update task');
    }
  }

  async function deleteTask(taskId) {
    if (!canUpdateTasks) return;
    if (!window.confirm('Delete this task?')) return;
    try {
      await apiFetch(`/equipment-tasks/${taskId}`, {
        method: 'DELETE'
      });
      await loadTasks();
    } catch (e) {
      alert(e.message || 'Failed to delete task');
    }
  }

  function getEquipmentList(type = newTask.equipmentType) {
    if (type === 'hull') return boatConfig.hulls;
    if (type === 'oars') return boatConfig.oars;
    if (type === 'cox_boxes') return boatConfig.coxBoxes;
    if (type === 'launch') return boatConfig.launches;
    if (type === 'speed_coach') return boatConfig.speedCoaches;
    return [];
  }

  function findAssetForTask(task) {
    if (!task?.equipment_id) return null;
    const list = getEquipmentList(task.equipment_type);
    const equipmentId = Number(task.equipment_id);
    return list.find(item => item.id === equipmentId) || null;
  }

  function handleEquipmentSelect(e) {
    const selectedId = e.target.value;
    const equipmentList = getEquipmentList();
    const selected = equipmentList.find(item => item.id === Number(selectedId));

    setNewTask({
      ...newTask,
      equipmentId: selectedId || '',
      equipmentName: selected ? selected.value : ''
    });
  }

  function getStatusBadgeClass(status) {
    switch (status) {
      case 'submitted': return 'status-badge submitted';
      case 'in_progress': return 'status-badge in-progress';
      case 'completed': return 'status-badge completed';
      default: return 'status-badge';
    }
  }

  function formatDate(dateString) {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  }

  const isCoach = (user?.role || '').toLowerCase() === 'coach';

  // Compact mode: only show the submit button
  const newTaskModal = newTaskModalOpen && (
    <div className="modalOverlay" onClick={closeNewTaskModal}>
      <div className="modalCard maintenanceModalSleek" onClick={(e) => e.stopPropagation()}>
        <button
          className="modal-close"
          aria-label="Close new maintenance task"
          onClick={closeNewTaskModal}
        >
          &times;
        </button>
        <div className="maintenanceModalHeader">
          <div className="maintenanceModalEyebrow">Equipment</div>
          <h3 className="maintenanceModalTitle">Report a maintenance task</h3>
        </div>
        <div className="maintenanceFormSleek">
          <label className="maintenanceField">
            <span>Equipment type</span>
            <select
              className="input"
              value={newTask.equipmentType}
              onChange={(e) => setNewTask({ ...newTask, equipmentType: e.target.value, equipmentId: '', equipmentName: '' })}
            >
              {assetTypeOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          {newTask.equipmentType !== 'none' && (
            <label className="maintenanceField">
              <span>Which one</span>
              <select
                className="input"
                value={newTask.equipmentId}
                onChange={handleEquipmentSelect}
              >
                <option value="">Select equipment...</option>
                {getEquipmentList().map(item => (
                  <option key={item.id} value={item.id}>
                    {item.value} {item.size ? `(${item.size})` : ''}
                  </option>
                ))}
              </select>
            </label>
          )}

          {newTask.equipmentType !== 'none' && (
            <label className="maintenanceField">
              <span>Or enter a name manually</span>
              <input
                className="input"
                placeholder="e.g. Empacher #3"
                value={newTask.equipmentName}
                onChange={(e) => setNewTask({ ...newTask, equipmentName: e.target.value })}
              />
            </label>
          )}

          <label className="maintenanceField">
            <span>What's wrong?</span>
            <textarea
              className="input"
              placeholder="Describe the damage or issue..."
              value={newTask.description}
              onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
              rows={3}
            />
          </label>

          <label className="maintenanceField">
            <span>Photo (optional)</span>
            {photoState.preview ? (
              <div className="oyoPhotoPreview">
                <img src={photoState.preview} alt="Selected maintenance issue" />
                <button className="btn ghost small pill" type="button" onClick={clearPhoto}>
                  Remove photo
                </button>
              </div>
            ) : (
              <div className="oyoPhotoDropzone">
                <input
                  key={photoInputKey}
                  className="oyoPhotoInput"
                  type="file"
                  accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={handlePhotoChange}
                />
                <span className="oyoPhotoDropzoneLabel">Tap to add a photo</span>
                <span className="oyoPhotoDropzoneHint">JPG, PNG, GIF or WebP · up to 5MB</span>
              </div>
            )}
          </label>

          <button className="btn oyoSubmitBtn" onClick={createTask}>Submit task</button>
        </div>
      </div>
    </div>
  );

  const editTaskModal = editingTask && (
    <div className="modalOverlay" onClick={closeEditModal}>
      <div className="modalCard roster-modal" onClick={(e) => e.stopPropagation()}>
        <button
          className="modal-close"
          aria-label="Close maintenance modal"
          onClick={closeEditModal}
        >
          &times;
        </button>
        <div className="roster-modal-header">
          <h3 className="h2 roster-modal-title">
            {canUpdateTasks ? "Update Maintenance Task" : "Maintenance Task Details"}
          </h3>
        </div>
        <div className="stack roster-modal-body">
          {(() => {
            const task = tasks.find(t => t.id === editingTask);
            if (!task) return null;
            const friendlyStatus = (task.status || editForm.status || "submitted")
              .replace(/_/g, " ")
              .replace(/\b\w/g, (c) => c.toUpperCase());
            const linkedAsset = findAssetForTask(task);
            const assetStatusLabel = linkedAsset
              ? (linkedAsset.statusName || "—")
              : "Linked asset not found in current boat config";
            const selectedAssetStatusMissing = !!(
              editForm.assetStatusId &&
              !assetStatuses.some((status) => String(status.id) === editForm.assetStatusId)
            );
            return (
              <>
                <div className="stack" style={{ gap: 8, padding: 12, background: 'var(--bg-elevated)', borderRadius: 8 }}>
                  <div>
                    <strong>{task.equipment_name || 'General Task'}</strong>
                    {task.equipment_type && task.equipment_type !== 'none' && (
                      <span className="help" style={{ marginLeft: 8 }}>
                        ({task.equipment_type})
                      </span>
                    )}
                  </div>
                  <p style={{ margin: 0 }}>{task.description}</p>
                  <div className="help" style={{ fontSize: '0.85em' }}>
                    Submitted by {task.submitted_by_name} on {formatDate(task.created_at)}
                  </div>
                  {task.photo_data_url ? (
                    <div className="stack" style={{ gap: 6 }}>
                      <span className="help">Attached Photo</span>
                      <img
                        src={task.photo_data_url}
                        alt="Maintenance attachment"
                        style={{ maxWidth: "100%", borderRadius: 8, border: "1px solid #e5e7eb" }}
                      />
                    </div>
                  ) : (
                    <span className="help">No photo attached</span>
                  )}
                </div>

                {task.equipment_id && (
                  <div className="stack" style={{ width: "100%" }}>
                    <span className="help">Asset Status</span>
                    {canEditAssetStatus ? (
                      assetStatuses.length > 0 ? (
                        <select
                          className="input"
                          value={editForm.assetStatusId}
                          onChange={(e) => setEditForm({ ...editForm, assetStatusId: e.target.value })}
                        >
                          <option value="">— No Status —</option>
                          {assetStatuses.map((status) => (
                            <option key={status.id} value={String(status.id)}>
                              {status.name}
                            </option>
                          ))}
                          {selectedAssetStatusMissing && (
                            <option value={editForm.assetStatusId}>
                              {linkedAsset?.statusName || 'Current status'}
                            </option>
                          )}
                        </select>
                      ) : (
                        <div className="stack" style={{ gap: 4 }}>
                          <div>{assetStatusLabel}</div>
                          <span className="help" style={{ color: '#b45309' }}>
                            Configure statuses under Config &gt; Asset Status to enable updates.
                          </span>
                        </div>
                      )
                    ) : (
                      <div className="stack" style={{ gap: canUpdateTasks ? 4 : 0 }}>
                        <div>{assetStatusLabel}</div>
                        {canUpdateTasks && (
                          <span className="help">
                            Asset status updates require configuration access.
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {canUpdateTasks && (
                  <label className="stack" style={{ width: "100%" }}>
                    <span className="help">Assign To</span>
                    <select
                      className="input"
                      value={editForm.assignedTo}
                      onChange={(e) => setEditForm({ ...editForm, assignedTo: e.target.value })}
                    >
                      <option value="">— Unassigned —</option>
                      {coaches.map((coach) => (
                        <option key={coach.id} value={String(coach.id)}>
                          {coach.name}
                        </option>
                      ))}
                    </select>
                  </label>
                )}

                {canUpdateTasks ? (
                  <label className="stack" style={{ width: "100%" }}>
                    <span className="help">Status</span>
                    <select
                      className="input"
                      value={editForm.status}
                      onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                    >
                      <option value="submitted">Submitted</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Completed</option>
                    </select>
                  </label>
                ) : (
                  <>
                    {task.assigned_to_name && (
                      <div className="stack" style={{ width: "100%" }}>
                        <span className="help">Assigned To</span>
                        <div>{task.assigned_to_name}</div>
                      </div>
                    )}
                    <div className="stack" style={{ width: "100%" }}>
                      <span className="help">Status</span>
                      <div>{friendlyStatus}</div>
                    </div>
                  </>
                )}

                {canUpdateTasks ? (
                  <label className="stack" style={{ width: "100%" }}>
                    <span className="help">Repair Notes</span>
                    <textarea
                      className="input"
                      style={{ minHeight: "80px", resize: "vertical", fontFamily: "inherit" }}
                      placeholder="Add repair notes..."
                      value={editForm.repairNotes}
                      onChange={(e) => setEditForm({ ...editForm, repairNotes: e.target.value })}
                    />
                  </label>
                ) : (
                  <div className="stack" style={{ width: "100%" }}>
                    <span className="help">Repair Notes</span>
                    <div>{task.repair_notes || "—"}</div>
                  </div>
                )}

                <div className="row" style={{ gap: 8, marginTop: 10, justifyContent: "space-between" }}>
               {isCoach && canUpdateTasks && (
                    <button
                      className="btn danger small"
                      onClick={() => {
                        closeEditModal();
                        deleteTask(task.id);
                      }}
                    >
                      Delete
                    </button>
                  )}
                  {canUpdateTasks && (
                    <button className="btn small" onClick={updateTaskStatus}>Save</button>
                  )}
                 
                </div>
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );

  if (compactMode) {
    return (
      <>
        <div className="card">
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 className="h2" style={{ margin: 0 }}>Maintenance Tasks</h3>
            <button className="btn" onClick={openNewTaskModal}>
              + Submit a Maintenance Task
            </button>
          </div>

          {error && (
            <p className="help" style={{ color: 'red', marginTop: 12 }}>
              Error: {error}
            </p>
          )}
        </div>
        {newTaskModal}
        {editTaskModal}
      </>
    );
  }

  const COLUMNS = [
    { status: 'submitted',   label: 'Submitted',   headerColor: 'var(--warn)',    headerBg: 'var(--warn-bg)',    headerBorder: 'var(--warn-border)' },
    { status: 'in_progress', label: 'In Progress', headerColor: '#93c5fd',        headerBg: 'rgba(59,130,246,0.12)', headerBorder: 'rgba(59,130,246,0.3)' },
    { status: 'completed',   label: 'Completed',   headerColor: 'var(--success)', headerBg: 'var(--success-bg)', headerBorder: 'var(--success-border)' },
  ];

  async function handleDrop(colKey) {
    const taskId = dragTaskId.current;
    dragTaskId.current = null;
    setDragOverCol(null);
    if (!taskId || !canUpdateTasks) return;
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    if (groupBy === 'status') {
      const targetStatus = colKey;
      if (task.status === targetStatus) return;
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: targetStatus } : t));
      try {
        await apiFetch(`/equipment-tasks/${taskId}`, {
          method: 'PATCH',
          body: JSON.stringify({ status: targetStatus, repairNotes: task.repair_notes || '' })
        });
      } catch (e) {
        setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: task.status } : t));
        alert(e.message || 'Failed to update task status');
      }
    } else {
      const targetAssigneeId = colKey === '__unassigned__' ? null : Number(colKey);
      if ((task.assigned_to ?? null) === targetAssigneeId) return;
      const targetName = assigneeColumns.find(c => (c.assigneeId ?? '__unassigned__') === colKey)?.label || null;
      setTasks(prev => prev.map(t => t.id === taskId ? { ...t, assigned_to: targetAssigneeId, assigned_to_name: targetAssigneeId ? targetName : null } : t));
      try {
        await apiFetch(`/equipment-tasks/${taskId}`, {
          method: 'PATCH',
          body: JSON.stringify({ assignedTo: targetAssigneeId ?? '' })
        });
      } catch (e) {
        setTasks(prev => prev.map(t => t.id === taskId ? { ...t, assigned_to: task.assigned_to, assigned_to_name: task.assigned_to_name } : t));
        alert(e.message || 'Failed to reassign task');
      }
    }
  }

  function KanbanColumn({ colKey, label, headerColor, headerBg, headerBorder, colTasks, isOver, canUpdateTasks, onDragOver, onDragLeave, onDrop }) {
    return (
      <div
        className="stack"
        style={{ gap: 8, minWidth: 0 }}
        onDragOver={(e) => { if (canUpdateTasks) { e.preventDefault(); onDragOver(e); } }}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 12px', borderRadius: 8, background: headerBg, border: `1px solid ${headerBorder}`,
        }}>
          <span style={{ fontWeight: 700, fontSize: '0.9em', color: headerColor, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {label}
          </span>
          <span style={{ fontWeight: 700, fontSize: '0.85em', color: headerColor, opacity: 0.8 }}>
            {colTasks.length}
          </span>
        </div>
        <div style={{
          display: 'flex', flexDirection: 'column', gap: 8, minHeight: 80, borderRadius: 8,
          padding: isOver ? 6 : 0,
          border: isOver ? `2px dashed ${headerBorder}` : '2px solid transparent',
          background: isOver ? headerBg : 'transparent',
          transition: 'all 0.15s ease',
        }}>
          {colTasks.length === 0 ? (
            <div style={{ padding: '16px 12px', textAlign: 'center', color: 'var(--muted)', fontSize: '0.85em', borderRadius: 8, border: isOver ? 'none' : '1px dashed var(--card-border)' }}>
              {isOver ? 'Drop here' : 'No tasks'}
            </div>
          ) : (
            colTasks.map(task => <KanbanCard key={task.id} task={task} />)
          )}
        </div>
      </div>
    );
  }

  function KanbanCard({ task }) {
    return (
      <div
        className="stack"
        draggable={canUpdateTasks}
        style={{
          gap: 8,
          cursor: canUpdateTasks ? 'grab' : 'pointer',
          background: 'var(--card)',
          border: '1px solid var(--card-border)',
          borderRadius: 8,
          padding: 12,
          boxShadow: 'var(--shadow-card)',
          userSelect: 'none',
        }}
        role="button"
        tabIndex={0}
        onDragStart={(e) => {
          dragTaskId.current = task.id;
          e.dataTransfer.effectAllowed = 'move';
        }}
        onDragEnd={() => { dragTaskId.current = null; setDragOverCol(null); }}
        onClick={() => openEditModal(task)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openEditModal(task); }
        }}
      >
        <div>
          <strong style={{ fontSize: '0.95em' }}>
            {task.equipment_name || 'General Task'}
          </strong>
          {task.equipment_type && task.equipment_type !== 'none' && (
            <span className="help" style={{ marginLeft: 6, fontSize: '0.8em' }}>({task.equipment_type})</span>
          )}
        </div>
        {groupBy === 'status' && task.assigned_to_name && (
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.8em',
            color: 'var(--primary)', fontWeight: 600, background: 'rgba(34,57,93,0.1)',
            border: '1px solid rgba(34,57,93,0.25)', borderRadius: 4, padding: '2px 7px', alignSelf: 'flex-start',
          }}>
            <span style={{ fontSize: '0.95em' }}>👤</span> {task.assigned_to_name}
          </div>
        )}
        {groupBy === 'assignee' && (
          <span className={getStatusBadgeClass(task.status)} style={{ alignSelf: 'flex-start', fontSize: '0.75em' }}>
            {(task.status || 'submitted').replace(/_/g, ' ').toUpperCase()}
          </span>
        )}
        <p style={{ margin: 0, fontSize: '0.88em', lineHeight: 1.4 }}>{task.description}</p>
        <div className="help" style={{ fontSize: '0.8em' }}>
          {formatDate(task.created_at)}
        </div>
        {task.repair_notes && (
          <div style={{ padding: '6px 8px', background: 'var(--success-bg)', borderRadius: 4, fontSize: '0.82em', color: 'var(--success)' }}>
            {task.repair_notes}
          </div>
        )}
      </div>
    );
  }

  // Full mode: kanban board
  return (
    <>
      <div className="card" style={{ marginBottom: 12 }}>
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
          <div className="row" style={{ gap: 10, alignItems: 'center' }}>
            <h3 className="h2" style={{ margin: 0 }}>Equipment Tasks</h3>
            <div style={{ display: 'flex', background: 'var(--bg)', border: '1px solid var(--card-border)', borderRadius: 20, padding: 2 }}>
              {[{ value: 'status', label: 'By Status' }, { value: 'assignee', label: 'By Assignee' }].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setGroupBy(opt.value)}
                  style={{
                    padding: '4px 14px',
                    borderRadius: 16,
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '0.82em',
                    fontWeight: 600,
                    transition: 'all 0.15s',
                    background: groupBy === opt.value ? 'var(--primary)' : 'transparent',
                    color: groupBy === opt.value ? '#fff7f9' : 'var(--muted)',
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div className="row" style={{ gap: 12, alignItems: 'center' }}>
            {groupBy === 'status' && (
              <>
                <label className="row" style={{ gap: 6, alignItems: 'center', cursor: 'pointer', margin: 0 }}>
                  <input
                    type="checkbox"
                    checked={myTasksOnly}
                    onChange={(e) => setMyTasksOnly(e.target.checked)}
                    style={{ cursor: 'pointer' }}
                  />
                  <span className="help" style={{ margin: 0, whiteSpace: 'nowrap' }}>My tasks</span>
                </label>
                <label className="row" style={{ gap: 6, alignItems: 'center', cursor: 'pointer', margin: 0 }}>
                  <input
                    type="checkbox"
                    checked={hideCompleted}
                    onChange={(e) => setHideCompleted(e.target.checked)}
                    style={{ cursor: 'pointer' }}
                  />
                  <span className="help" style={{ margin: 0, whiteSpace: 'nowrap' }}>Hide completed</span>
                </label>
              </>
            )}
            <button className="btn" onClick={openNewTaskModal}>+ New Task</button>
          </div>
        </div>
        {error && <p className="help" style={{ color: 'red', marginTop: 8, marginBottom: 0 }}>Error: {error}</p>}
      </div>

      {loading ? (
        <p className="help" style={{ padding: 16 }}>Loading tasks...</p>
      ) : tasks.length === 0 ? (
        <p className="help" style={{ textAlign: 'center', padding: 16 }}>No equipment tasks yet. Report an issue above!</p>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: groupBy === 'assignee'
            ? `repeat(${Math.max(assigneeColumns.length, 1)}, minmax(220px, 1fr))`
            : 'repeat(3, 1fr)',
          gap: 16,
          alignItems: 'start',
        }}
          className="kanban-board"
        >
          {groupBy === 'status' ? COLUMNS.map(col => {
            if (hideCompleted && col.status === 'completed') return null;
            const colKey = col.status;
            const colTasks = tasks.filter(t =>
              (t.status || 'submitted') === col.status &&
              (!myTasksOnly || t.assigned_to === user?.id)
            );
            const isOver = dragOverCol === colKey;
            return (
              <KanbanColumn
                key={colKey}
                colKey={colKey}
                label={col.label}
                headerColor={col.headerColor}
                headerBg={col.headerBg}
                headerBorder={col.headerBorder}
                colTasks={colTasks}
                isOver={isOver}
                canUpdateTasks={canUpdateTasks}
                onDragOver={() => setDragOverCol(colKey)}
                onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverCol(null); }}
                onDrop={() => handleDrop(colKey)}
              />
            );
          }) : assigneeColumns.map(col => {
            const colKey = col.assigneeId === null ? '__unassigned__' : String(col.assigneeId);
            const colTasks = tasks.filter(t => (t.assigned_to ?? null) === col.assigneeId);
            const isOver = dragOverCol === colKey;
            return (
              <KanbanColumn
                key={colKey}
                colKey={colKey}
                label={col.label}
                headerColor={col.headerColor}
                headerBg={col.headerBg}
                headerBorder={col.headerBorder}
                colTasks={colTasks}
                isOver={isOver}
                canUpdateTasks={canUpdateTasks}
                onDragOver={() => setDragOverCol(colKey)}
                onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverCol(null); }}
                onDrop={() => handleDrop(colKey)}
              />
            );
          })}
        </div>
      )}

      {newTaskModal}
      {editTaskModal}
    </>
  );
}
